/**
 * Pure editor helpers for a mock API spec — the client-side mirror of
 * services/mockapi/authoring.js's shape, with no validation of its own.
 *
 * Validation stays on the server deliberately: a second copy of the rules here would
 * drift, and the editor already round-trips through POST /api/mock-api/validate, which
 * is the same `checkMockSpec` the save path runs. These functions only move objects
 * around, so they are trivially testable and cannot disagree with anything.
 */

export const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'ANY'];
export const DEFAULT_SCENARIO = 'default';

/** A new case, pre-shaped as the catch-all — which is what a route needs to answer at
 *  all, and the thing authors most often forget. */
export function emptyCase(overrides = {}) {
  return {
    name: '',
    scenario: DEFAULT_SCENARIO,
    match: {},
    status: 200,
    headers: {},
    body: '{\n  "ok": true\n}',
    bodyMode: 'static',
    contentType: 'application/json',
    delayMs: 0,
    ...overrides,
  };
}

export function emptyRoute(overrides = {}) {
  return {
    name: '', description: '', method: 'GET', path: '/', cases: [emptyCase()], ...overrides,
  };
}

export function emptySpec() {
  return { routes: [] };
}

/** Immutable list edits — the editor holds the spec in React state, so every helper
 *  returns a new object rather than mutating in place. */
export function addRoute(spec, route = emptyRoute()) {
  return { ...spec, routes: [...(spec.routes || []), route] };
}

export function updateRoute(spec, index, patch) {
  return {
    ...spec,
    routes: (spec.routes || []).map((r, i) => (i === index ? { ...r, ...patch } : r)),
  };
}

export function removeRoute(spec, index) {
  return { ...spec, routes: (spec.routes || []).filter((_, i) => i !== index) };
}

export function moveRoute(spec, from, to) {
  const routes = [...(spec.routes || [])];
  if (from < 0 || to < 0 || from >= routes.length || to >= routes.length) return spec;
  const [moved] = routes.splice(from, 1);
  routes.splice(to, 0, moved);
  return { ...spec, routes };
}

export function addCase(spec, routeIndex, kase = emptyCase()) {
  return updateRoute(spec, routeIndex, {
    cases: [...((spec.routes || [])[routeIndex]?.cases || []), kase],
  });
}

export function updateCase(spec, routeIndex, caseIndex, patch) {
  const route = (spec.routes || [])[routeIndex];
  if (!route) return spec;
  return updateRoute(spec, routeIndex, {
    cases: (route.cases || []).map((c, i) => (i === caseIndex ? { ...c, ...patch } : c)),
  });
}

export function removeCase(spec, routeIndex, caseIndex) {
  const route = (spec.routes || [])[routeIndex];
  if (!route) return spec;
  return updateRoute(spec, routeIndex, {
    cases: (route.cases || []).filter((_, i) => i !== caseIndex),
  });
}

/** Every scenario the spec actually uses, `default` first. Drives the scenario switcher
 *  without the author having to remember what they named things. */
export function scenariosIn(spec) {
  const names = (spec.routes || []).flatMap((r) => (r.cases || []).map((c) => c.scenario || DEFAULT_SCENARIO));
  return [DEFAULT_SCENARIO, ...[...new Set(names)].filter((n) => n !== DEFAULT_SCENARIO).sort()];
}

/** Does this route answer every request that reaches it? A route whose every case has a
 *  matcher falls through to the environment fallback, which reads as a broken mock. */
export function hasCatchAll(route) {
  return (route.cases || []).some((c) => !c.match || Object.keys(c.match).length === 0);
}

/** A one-line summary of a matcher, for the collapsed case row. */
export function describeMatch(match) {
  if (!match || !Object.keys(match).length) return 'any request';
  const parts = [];
  for (const [name, value] of Object.entries(match.query || {})) parts.push(`?${name}=${value}`);
  for (const [name, value] of Object.entries(match.headers || {})) parts.push(`${name}: ${value}`);
  for (const [name, value] of Object.entries(match.params || {})) parts.push(`:${name}=${value}`);
  for (const rule of match.jsonPath || []) parts.push(`body.${rule.path}=${rule.equals}`);
  for (const needle of match.bodyContains || []) parts.push(`body contains "${needle}"`);
  return parts.join(' · ') || 'any request';
}

/** Round-trip a matcher through the JSON textarea the editor exposes for it. Returns
 *  the previous value on a parse failure so a half-typed edit never wipes the matcher. */
export function parseMatch(text, fallback = {}) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

/** Is this text valid JSON? Used to warn in the editor before the server does. */
export function isJsonish(text) {
  if (!String(text || '').trim()) return true;
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}
